import { useCallback, useEffect, useRef } from 'react'

/**
 * Hook to manage default button focus in Dialog
 * - Sets focus to the default button if one is marked with isDefault
 * - Falls back to cancel button if it exists and no default is set
 */
export const useDialogDefaultButton = (isOpen: boolean) => {
  const defaultButtonRef = useRef<HTMLButtonElement | null>(null)
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null)

  const registerDefaultButton = useCallback((ref: HTMLButtonElement | null) => {
    defaultButtonRef.current = ref
  }, [])

  const registerCancelButton = useCallback((ref: HTMLButtonElement | null) => {
    cancelButtonRef.current = ref
  }, [])

  useEffect(() => {
    if (!isOpen) return

    // Use requestAnimationFrame to ensure buttons are rendered and refs are set
    const frameId = requestAnimationFrame(() => {
      // Set focus on mount: priority to default button, fallback to cancel button
      if (defaultButtonRef.current) {
        defaultButtonRef.current.focus()
      } else if (cancelButtonRef.current) {
        cancelButtonRef.current.focus()
      }
    })

    return () => cancelAnimationFrame(frameId)
  }, [isOpen])

  return {
    registerDefaultButton,
    registerCancelButton,
  }
}
